/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T11:00:00+05:00
Intent: Read what a participant typed on a text level that accepts any answer, so the campaign's participants table
and the Participants page (narrowed to one campaign) can show it.
*/

export type TextAnswer =
  | { kind: 'answer'; text: string }
  | { kind: 'withheld'; mode: string }
  | { kind: 'none' };

const NONE: TextAnswer = { kind: 'none' };

/** A text level whose every answer is accepted: what the participant typed is the result. */
export function isAcceptAnyTextLevel(level: any): boolean {
  const type = String(level?.level_type || level?.type || '').toLowerCase();
  const acceptAny = level?.accept_any_answer;
  return type === 'text' && (acceptAny === true || acceptAny === 1 || String(acceptAny).toLowerCase() === 'true');
}

/** The answer as one line of text, whatever shape it was stored in. */
function answerText(value: any): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(answerText).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    return answerText(value.answer_text ?? value.answer ?? value.text ?? value.value ?? null);
  }
  return String(value).replace(/\s+/g, ' ').trim();
}

// The names the server has used for the submitted text, current first.
const pickAnswer = (entry: any) => entry?.user_answer ?? entry?.answer ?? entry?.text_answer
  ?? entry?.submitted_answer ?? entry?.submitted_text ?? entry?.submission ?? entry?.response ?? null;

/** What the participant answered on the level, read from `levels_detail`, or the older `users_progress` on the level. */
export function textLevelAnswer(participant: any, levelId: any): TextAnswer {
  if (!participant || levelId === null || levelId === undefined) return NONE;
  const id = String(levelId);
  const details: any[] = participant.levels_detail || participant.levelsDetail || [];
  const detail = details.find(entry => String(entry?.level_id ?? entry?.id) === id);
  if (detail?.answers_withheld) return { kind: 'withheld', mode: String(detail.answers_withheld) };

  let text = answerText(pickAnswer(detail));
  if (!text) {
    const userId = participant._end_user?.id ?? participant.user_id ?? participant.participant_id;
    const levels: any[] = participant.contest?.levels || participant.contest?._levels_of_contest || [];
    const level = levels.find(entry => String(entry?.id) === id);
    const progress = (level?.users_progress || [])
      .find((entry: any) => userId !== undefined && String(entry?.user_id) === String(userId));
    text = answerText(pickAnswer(progress));
  }
  return text ? { kind: 'answer', text } : NONE;
}

/** The campaign's text levels that accept any answer, in the order the client set the levels. */
export function acceptAnyTextLevels(levels: any): any[] {
  return (Array.isArray(levels) ? levels : [])
    .filter(isAcceptAnyTextLevel)
    .map((level, index) => ({ level, index, position: Number(level?.position ?? level?.order) }))
    .sort((a, b) => (Number.isFinite(a.position) && Number.isFinite(b.position) ? a.position - b.position : 0) || a.index - b.index)
    .map(entry => entry.level);
}

/** Who the participant row belongs to: the end user's id, which every participant list carries in one of these names. */
export function participantUserKey(participant: any): string {
  const unwrap = (user: any) => (Array.isArray(user) ? user[0] : user) || {};
  const id = unwrap(participant?._end_user).id ?? unwrap(participant?.end_user).id
    ?? participant?.end_user_id ?? participant?.user_id ?? participant?.participant_id;
  return id === null || id === undefined || id === '' ? '' : String(id);
}

/** What each participant of a list answered on the given levels, by participantUserKey then level id. */
export function indexTextAnswers(participants: any[], levels: any[]): Map<string, Map<string, TextAnswer>> {
  const index = new Map<string, Map<string, TextAnswer>>();
  for (const participant of participants || []) {
    const key = participantUserKey(participant);
    if (!key) continue;
    const answers = index.get(key) ?? new Map<string, TextAnswer>();
    for (const level of levels) {
      const answer = textLevelAnswer(participant, level?.id);
      if (answer.kind !== 'none' && !answers.has(String(level.id))) answers.set(String(level.id), answer);
    }
    if (answers.size) index.set(key, answers);
  }
  return index;
}
